import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";
import Head from "next/head";

import { RegionParlorsContainer } from "@/containers/RegionParlorsContainer";
import {
  buildRegionPageProps,
  RegionPageProps,
} from "@/utils/buildRegionPageProps";
import { listRegionPageRegions } from "@/utils/regionFacilities";
import { skipDevBuildInGetStaticPaths } from "@/utils/skipDevBuildInGetStaticPaths";

/**
 * 지역 장지 페이지. 장지가 메인이고 인근 장례식장, 화장장을 같은 페이지에 묶는다.
 * /region/parlor/* 와 데이터는 같고 focus 로 섹션과 카피 순서만 바꾼다.
 *
 * 이 페이지가 /facility/{id}/ 상세로 들어가는 진입점이다. 상세만 만들어 두면
 * 내부 링크가 없어서 색인이 안 된다.
 *
 * URL 단위는 시군구. 생성 대상은 parlor 쪽과 같게 두었다. 장지만 있는 시군구를
 * 따로 추리려면 listRegionPageRegions 대신 장지 기준 목록이 필요한데,
 * 두 페이지의 생성 대상이 갈리면 nearbyRegions 링크가 404 로 빠진다.
 */
export const getStaticPaths: GetStaticPaths = async () => {
  const skipBuildResult =
    skipDevBuildInGetStaticPaths() as GetStaticPathsResult;

  if (skipBuildResult) {
    return skipBuildResult;
  }

  return {
    paths: listRegionPageRegions().map((region) => ({
      params: { sido: region.sido, sigungu: region.sigungu },
    })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const sido = params?.sido;
  const sigungu = params?.sigungu;

  if (typeof sido !== "string" || typeof sigungu !== "string") {
    return { notFound: true };
  }

  const props = buildRegionPageProps(sido, sigungu, "facility");

  if (!props) {
    return { notFound: true };
  }

  return {
    props: {
      ...props,
      pagePublishedTime: "2026-08-05T00:00:00+09:00",
      // CDN_URL이 없는 환경에서 "undefined/ogimage.png"가 og:image로 나가지 않게 한다
      ...(process.env.NEXT_PUBLIC_CDN_URL
        ? { pageOgImage: `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png` }
        : {}),
    },
  };
};

const RegionFacilitiesPage = ({ pageJsonLd, ...data }: RegionPageProps) => {
  return (
    <>
      {pageJsonLd && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: pageJsonLd }}
          />
        </Head>
      )}
      <RegionParlorsContainer {...data} focus="facility" />
    </>
  );
};

export default RegionFacilitiesPage;
